import { onCLS, onFCP, onLCP, onTTFB, onINP, type Metric } from 'web-vitals';

/**
 * Web Vitals Monitoring Utility
 * 
 * Tracks Core Web Vitals and sends metrics to analytics:
 * - LCP (Largest Contentful Paint): <2.5s good, >4s poor
 * - FCP (First Contentful Paint): <1.8s good, >3s poor
 * - CLS (Cumulative Layout Shift): <0.1 good, >0.25 poor
 * - TTFB (Time to First Byte): <800ms good, >1800ms poor
 * - INP (Interaction to Next Paint): <200ms good, >500ms poor
 */

interface PerformanceData {
    name: string;
    value: number;
    rating: 'good' | 'needs-improvement' | 'poor';
    delta: number;
    id: string;
    navigationType: string;
}

/**
 * Send metric to analytics
 */
function sendToAnalytics(metric: Metric) {
    const body: PerformanceData = {
        name: metric.name,
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
        id: metric.id,
        navigationType: metric.navigationType,
    };

    // Log to console in development
    if (import.meta.env.DEV) {
        console.log('[Web Vitals]', metric.name, {
            value: `${metric.value.toFixed(2)}ms`,
            rating: metric.rating,
            delta: `${metric.delta.toFixed(2)}ms`,
        });
    }

    // Send to Firebase Analytics if available
    if (typeof window !== 'undefined' && 'gtag' in window) {
        const gtag = (window as any).gtag;
        gtag('event', metric.name, {
            value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
            event_category: 'Web Vitals',
            event_label: metric.id,
            non_interaction: true,
        });
    }

    // Send to custom analytics endpoint (optional)
    if (import.meta.env.PROD) {
        // Or store locally for debugging
        const vitals = JSON.parse(localStorage.getItem('web-vitals') || '[]');
        vitals.push({
            ...body,
            timestamp: new Date().toISOString(),
            url: window.location.pathname,
            userAgent: navigator.userAgent,
        });
        // Keep only last 50 metrics
        if (vitals.length > 50) vitals.shift();
        localStorage.setItem('web-vitals', JSON.stringify(vitals));
    }
}

/**
 * Initialize Web Vitals monitoring
 * Call this once when your app starts
 */
export function initWebVitals() {
    // Track all Core Web Vitals
    onCLS(sendToAnalytics);  // Cumulative Layout Shift
    onFCP(sendToAnalytics);  // First Contentful Paint
    onLCP(sendToAnalytics);  // Largest Contentful Paint
    onTTFB(sendToAnalytics); // Time to First Byte
    onINP(sendToAnalytics);  // Interaction to Next Paint

    if (import.meta.env.DEV) {
        console.log('✅ Web Vitals monitoring initialized');
    }
}

/**
 * Get stored Web Vitals metrics
 */
export function getStoredVitals() {
    if (typeof window === 'undefined') return [];
    return JSON.parse(localStorage.getItem('web-vitals') || '[]');
}

/**
 * Clear stored Web Vitals metrics
 */
export function clearStoredVitals() {
    if (typeof window !== 'undefined') {
        localStorage.removeItem('web-vitals');
    }
}

type RatingType = 'good' | 'needs-improvement' | 'poor';

/**
 * Get performance summary
 */
export function getPerformanceSummary() {
    const vitals = getStoredVitals();

    const summary = {
        LCP: { values: [] as number[], avg: 0, rating: 'good' as RatingType },
        FCP: { values: [] as number[], avg: 0, rating: 'good' as RatingType },
        CLS: { values: [] as number[], avg: 0, rating: 'good' as RatingType },
        INP: { values: [] as number[], avg: 0, rating: 'good' as RatingType },
        TTFB: { values: [] as number[], avg: 0, rating: 'good' as RatingType },
    };

    vitals.forEach((vital: any) => {
        if (vital.name in summary) {
            summary[vital.name as keyof typeof summary].values.push(vital.value);
        }
    });

    // Calculate averages and ratings
    Object.keys(summary).forEach((key) => {
        const metric = summary[key as keyof typeof summary];
        if (metric.values.length > 0) {
            metric.avg = metric.values.reduce((a, b) => a + b, 0) / metric.values.length;

            // Determine rating based on thresholds
            if (key === 'LCP') {
                metric.rating = metric.avg < 2500 ? 'good' : metric.avg < 4000 ? 'needs-improvement' : 'poor';
            } else if (key === 'FCP') {
                metric.rating = metric.avg < 1800 ? 'good' : metric.avg < 3000 ? 'needs-improvement' : 'poor';
            } else if (key === 'CLS') {
                metric.rating = metric.avg < 0.1 ? 'good' : metric.avg < 0.25 ? 'needs-improvement' : 'poor';
            } else if (key === 'INP') {
                metric.rating = metric.avg < 200 ? 'good' : metric.avg < 500 ? 'needs-improvement' : 'poor';
            } else if (key === 'TTFB') {
                metric.rating = metric.avg < 800 ? 'good' : metric.avg < 1800 ? 'needs-improvement' : 'poor';
            }
        }
    });

    return summary;
}
